import { appSwitchRoutes } from 'constants/routes';
import { matchPath } from 'react-router-dom';

type TRoleWithLiveChat = {
  id: string;
  company: { isLiveChatEnabled: boolean };
};

const getRoleIdFromPath = (pathname: string): string | undefined =>
  matchPath(
    { path: `${appSwitchRoutes.roles.path}/:roleId`, end: false },
    pathname
  )?.params.roleId;

/**
 * Whether the live chat may show on the given page, from the candidate's
 * roles and their companies' "Display live chat to users" setting.
 *
 * Inside one role's pages (`/roles/:roleId/...`) the chat follows that role's
 * company. Everywhere else the candidate spans companies, so the chat shows
 * while any role's company still has it on. An unknown list (still loading,
 * or the request failed) keeps the default: shown.
 */
export const isLiveChatEnabledOnPath = (
  pathname: string,
  roles: ReadonlyArray<TRoleWithLiveChat> | null | undefined
): boolean => {
  if (!roles || roles.length === 0) {
    return true;
  }
  const roleId = getRoleIdFromPath(pathname);
  if (roleId) {
    const role = roles.find(({ id }) => id === roleId);
    return role?.company.isLiveChatEnabled ?? true;
  }
  return roles.some(role => role.company.isLiveChatEnabled);
};

// Pages where the candidate is in the middle of an assessment and must not be
// distracted by the chat.
const ASSESSMENT_PATHS = [
  appSwitchRoutes.roleStagePersonalityTest.path,
  appSwitchRoutes.roleStageCognitiveTest.path,
  appSwitchRoutes.roleStageEnglishProficiencyTest.path,
  appSwitchRoutes.roleStageRssAssessment.path,
  appSwitchRoutes.aiInterview.path,
];

const isAssessmentPath = (pathname: string): boolean =>
  ASSESSMENT_PATHS.some(path => matchPath({ path, end: false }, pathname));

/**
 * The launcher stays away during an assessment, and wherever the company
 * has switched the live chat off.
 */
export const shouldHideIntercomLauncher = (
  pathname: string,
  roles: ReadonlyArray<TRoleWithLiveChat> | null | undefined
): boolean =>
  isAssessmentPath(pathname) || !isLiveChatEnabledOnPath(pathname, roles);
